import { useMemo } from 'react';

import { type QueryOptions } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Alert, Box } from '@mui/material';
import { green, grey, red } from '@mui/material/colors';
import type { GridColDef, GridRowsProp } from '@mui/x-data-grid-premium';
import { getReviewRatingSummary } from '@realadvisor/agency-utils';
import { MapPinIcon, MapPinOffIcon, Search } from 'lucide-react';
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import {
  type GetTeamsListQuery,
  Order_By,
  type TeamCombinedReviewsAggFragment,
  type Teams_Bool_Exp,
  type Teams_Order_By,
} from '@graphql/__generated__/graphql';
import { AddressCell } from '@modules/_shared/components/data-grid/AddressCell';
import { OrganisationCell } from '@modules/_shared/components/data-grid/OrganisationCell';
import { RaDataGrid } from '@modules/_shared/components/data-grid/RaDataGrid';
import { SelectStartIcon } from '@modules/_shared/components/form/SelectStartIcon';
import { ListToolbar } from '@modules/_shared/components/list-toolbar/ListToolbar';
import RatingDisplay from '@modules/_shared/components/RatingDisplay';
import { SmartListTitle } from '@modules/_shared/components/SmartListTitle';
import { Tag } from '@modules/_shared/components/Tag';
import { TeamAvatar } from '@modules/_shared/components/TeamAvatar';
import { TeamsSelect } from '@modules/_shared/components/teams-select/TeamsSelect';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  parseOrderByParam,
  withTiebreaker,
} from '@modules/_shared/utils/listUrlParams';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import { prepareWhereClauseQuery } from '@modules/_shared/utils/parseWhereClause';
import { getAgentFilter } from '@modules/_shared/utils/quickFilters';
import { TeamCreateDrawer } from '@modules/teams/TeamCreateDrawer';
import { TeamDrawer } from '@modules/teams/TeamDrawer';
import {
  GET_TEAMS_LIST,
  GET_TEAMS_LIST_COUNT,
} from '@modules/teams/teamQueries';
import { useLocale } from '@shared/hooks/locale';

type Team = GetTeamsListQuery['teams'][number];
type CombinedReviewsAggregate =
  TeamCombinedReviewsAggFragment['combined_reviews_aggregate'];

export const initialTeamsListQuery = (
  searchParams: URLSearchParams,
): QueryOptions => {
  const where: Teams_Bool_Exp = prepareWhereClauseQuery(
    JSON.parse(searchParams.get('where') || '{}'),
  );
  const order_by = withTiebreaker<Teams_Order_By>(
    parseOrderByParam<Teams_Order_By>(searchParams.get('order_by'), [
      { created_at: Order_By.Desc },
    ]),
    { id: Order_By.Asc },
    'id',
  );
  const { limit, offset } = getPaginationVariables(searchParams);

  return {
    query: GET_TEAMS_LIST,
    variables: { where, order_by, limit, offset },
  };
};

export function getRatingNumberAndAverage(
  teamCombinedReviewsAgg: CombinedReviewsAggregate,
  usersCombinedReviewsAgg: CombinedReviewsAggregate[],
) {
  return getReviewRatingSummary(
    {
      averageRating: teamCombinedReviewsAgg.aggregate?.avg?.rating,
      reviewCount: teamCombinedReviewsAgg.aggregate?.count,
    },
    usersCombinedReviewsAgg.map(review => ({
      averageRating: review?.aggregate?.avg?.rating,
      reviewCount: review?.aggregate?.count,
    })),
  );
}

export const Teams = () => {
  const { t, locale } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { me } = useAppData();

  const { variables } = useMemo(
    () => initialTeamsListQuery(searchParams),
    [searchParams],
  );

  const { where, order_by, limit, offset } = variables ?? {};

  const { data, loading, error } = useQuery(GET_TEAMS_LIST, {
    variables: {
      where,
      order_by,
      limit,
      offset,
    },
  });
  const teams: GridRowsProp<Team> = data?.teams ?? [];

  const { data: countData } = useQuery(GET_TEAMS_LIST_COUNT, {
    variables: {
      where,
    },
  });

  const columns: GridColDef<Team>[] = [
    {
      field: 'created_at',
      headerName: t('Created'),
      width: 75,
      renderCell: ({ value }) => <TimeAgo dateString={value} />,
    },
    {
      field: 'name',
      headerName: t('Name'),
      minWidth: 250,
      maxWidth: 500,
      flex: 1,
      renderCell: ({ row }) => (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div style={{ position: 'relative' }}>
            <TeamAvatar logoUrl={row?.logo?.url} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div>{row?.name}</div>
          </div>
        </div>
      ),
    },
    {
      field: 'teams_users.user.subscriptions.status',
      headerName: t('Subscription'),
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <>
          <div
            style={{
              height: 10,
              width: 10,
              borderRadius: '50%',
              display: 'inline-block',
              flexShrink: 0,
              marginRight: 5,
              backgroundColor:
                row.teams_users.filter(
                  tu =>
                    tu.user?.subscriptions.filter(s => s.status === 'active')
                      .length > 0,
                ).length > 0
                  ? green[500]
                  : grey[300],
            }}
          />
          <div
            style={{
              flexGrow: 1,
              textAlign: 'right',
            }}
          >
            {(
              row.teams_users.reduce((acc, tu) => {
                return (
                  acc +
                  tu.user?.subscriptions
                    .filter(s => s.status === 'active')
                    .reduce((acc, s) => acc + s.total_amount, 0)
                );
              }, 0) / 100
            ).toLocaleString(locale)}
          </div>
        </>
      ),
    },
    {
      field: 'show_in_agency_pages',
      headerName: t('Show in agency pages'),
      width: 350,
      display: 'flex',

      renderCell: ({ row }) => (
        <Tag status={row.show_in_agency_pages ? 'success' : 'neutral'}>
          /{row.agency_slug}
        </Tag>
      ),
    },
    {
      field: 'postcode,locality,route,street_number',
      headerName: t('Address'),
      width: 200,
      display: 'flex',

      renderCell: ({ row }) => (
        <>
          {row.lat && row.lng ? (
            <MapPinIcon
              size={20}
              style={{ marginRight: 8, flexShrink: 0 }}
              color={red[700]}
            />
          ) : (
            <MapPinOffIcon
              size={20}
              style={{ marginRight: 8, flexShrink: 0 }}
              color={grey[300]}
            />
          )}
          <AddressCell
            address={{
              street_number: row.street_number ?? '',
              route: row.route ?? '',
              postcode: row.postcode ?? '',
              locality: row.locality ?? '',
            }}
          />
        </>
      ),
    },
    {
      field: 'property_transactions_aggregate.count',
      headerName: t('Transactions'),
      width: 120,
      align: 'right',
      renderCell: ({ row }) =>
        row.property_transactions_aggregate?.aggregate?.count,
    },
    {
      field: 'property_transactions_aggregate.max.updated_at',
      headerName: t('Last transaction'),
      width: 120,
      renderCell: ({ row }) => (
        <TimeAgo
          dateString={
            row.property_transactions_aggregate?.aggregate?.max?.updated_at
          }
        />
      ),
    },
    {
      field: 'teams_users.user.lead_agents_aggregate.aggregate.count',
      sortable: false,
      headerName: t('Seller leads'),
      width: 120,
      align: 'right',
      renderCell: ({ row }) =>
        row.teams_users.reduce((acc, tu) => {
          return acc + (tu.user?.lead_agents_aggregate?.aggregate?.count ?? 0);
        }, 0),
    },
    {
      field: 'combined_rating',
      headerName: t('Combined Reviews'),
      width: 200,
      display: 'flex',
      sortable: false,
      renderCell: ({ row }) => {
        const { totalReviews, averageRating } = getRatingNumberAndAverage(
          row.combined_reviews_aggregate,
          row.teams_users.map(tu => tu.user?.combined_reviews_aggregate),
        );

        return (
          <div>
            {totalReviews > 0 && (
              <Box sx={{ gap: 1, alignItems: 'center', display: 'flex' }}>
                <RatingDisplay
                  readOnly
                  count={totalReviews}
                  rating={averageRating}
                  size="small"
                />
              </Box>
            )}
          </div>
        );
      },
    },
    {
      field: 'google_reviews_aggregate.avg.rating',
      headerName: t('Google Reviews'),
      width: 200,
      display: 'flex',
      renderCell: ({ row }) => (
        <div>
          <div>{row.google_place_description ?? ''}</div>
          {row.google_reviews_aggregate?.aggregate?.avg?.rating && (
            <Box sx={{ gap: 1, alignItems: 'center', display: 'flex' }}>
              <RatingDisplay
                count={row.google_reviews_aggregate?.aggregate?.count}
                readOnly
                rating={row.google_reviews_aggregate?.aggregate?.avg?.rating}
                size="small"
              />
            </Box>
          )}
        </div>
      ),
    },
    {
      field: 'teams_users_aggregate.count',
      headerName: t('Users'),
      width: 200,
      renderCell: ({ row }) => {
        return row?.teams_users?.map(tu => tu.user?.full_name).join(', ');
      },
    },
    {
      field: 'organisation.name',
      headerName: t('Organisation'),
      width: 250,
      renderCell: ({ row }) =>
        row.organisation ? (
          <OrganisationCell organisation={row.organisation} />
        ) : null,
    },
  ];

  return (
    <>
      <SmartListTitle tableName="teams" defaultTitle={t('Agencies / Teams')} />

      <ListToolbar
        tableName="teams"
        quickFilters={[
          {
            label: t('Subscription status'),
            path: [
              'teams_bool_exp',
              'teams_users',
              'user',
              'subscriptions',
              'status',
              '_in',
            ],
          },
          {
            label: t('Subscription amount'),
            path: [
              'teams_bool_exp',
              'teams_users',
              'user',
              'subscriptions',
              'total_amount',
            ],
          },
          {
            label: t('Show in agency pages'),
            path: ['teams_bool_exp', 'show_in_agency_pages'],
          },
          {
            label: t('Places'),
            path: ['teams_bool_exp', 'places'],
          },
          {
            label: t('Agents'),
            path: ['teams_bool_exp', 'teams_users', 'user'],
            filter: getAgentFilter(me),
          },
        ]}
        searchComponent={
          <TeamsSelect
            autoFocus
            InputProps={{
              startAdornment: <SelectStartIcon icon={Search} />,
            }}
            sx={{
              maxWidth: 350,
              flexGrow: 1,
              ml: 1,
            }}
            onChange={teamId => {
              if (!teamId) {
                return;
              }
              navigate({
                pathname: `/teams/${teamId}`,
                search: searchParams.toString(),
              });
            }}
            size="small"
          />
        }
        newLink="new"
      />
      {error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      ) : (
        <RaDataGrid
          loading={loading}
          rows={teams}
          columns={columns}
          rowCount={countData?.teams_aggregate.aggregate?.count ?? 0}
        />
      )}
      <Routes>
        <Route
          path="new"
          element={
            me?.is_admin ? (
              <TeamCreateDrawer />
            ) : (
              <Navigate to="../" replace={true} />
            )
          }
        />
        <Route path=":teamId/*" element={<TeamDrawer />} />
      </Routes>
    </>
  );
};

export default Teams;
